/**
 * Console iconography — one consistent line-icon set (18px viewBox 24,
 * stroke 1.8, round caps). Import `Icon` and name glyphs; never inline
 * one-off SVGs in pages anymore.
 */
import type { CSSProperties } from 'react';

export type IconName =
  | 'dashboard'
  | 'queue'
  | 'case'
  | 'approvals'
  | 'customers'
  | 'handover'
  | 'proactive'
  | 'housing'
  | 'services'
  | 'builder'
  | 'agents'
  | 'documents'
  | 'payments'
  | 'govsync'
  | 'notifications'
  | 'audit'
  | 'admin'
  | 'integrations'
  | 'cxone'
  | 'search'
  | 'menu'
  | 'settings'
  | 'run'
  | 'sparkle'
  | 'clock'
  | 'shield'
  | 'chart'
  | 'pulse'
  | 'link'
  | 'download'
  | 'filter'
  | 'plus'
  | 'check'
  | 'x'
  | 'arrow-start'
  | 'arrow-end'
  | 'warning'
  | 'info'
  | 'copy'
  | 'print'
  | 'refresh'
  | 'external'
  | 'pin'
  | 'star'
  | 'user'
  | 'users'
  | 'lock';

const PATHS: Record<IconName, string> = {
  dashboard: 'M4 4h7v7H4zM13 4h7v4h-7zM13 11h7v9h-7zM4 14h7v6H4z',
  queue: 'M4 6h16M4 12h16M4 18h10',
  case: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M9 13h6M9 17h4',
  approvals: 'M12 3l7 3v5c0 4.6-3 8.4-7 10-4-1.6-7-5.4-7-10V6zM9 12l2 2 4-4',
  customers: 'M16 19a4 4 0 0 0-8 0M12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19 19a5.5 5.5 0 0 0-3-4.9M5 19a5.5 5.5 0 0 1 3-4.9M15.5 8.5a2.5 2.5 0 1 1 3 3.5M8.5 8.5a2.5 2.5 0 1 0-3 3.5',
  handover: 'M21 11.5a8.5 8.5 0 0 1-12.4 7.5L3 21l2-5.6A8.5 8.5 0 1 1 21 11.5zM8.5 10.5h.01M12 10.5h.01M15.5 10.5h.01',
  proactive: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
  housing: 'm3 11 9-7 9 7M5 10v10h14V10M10 20v-6h4v6',
  services: 'M4 7h16M4 7v12a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V7M4 7l2-4h12l2 4M10 12h4',
  builder: 'M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.2 2.2M16.2 16.2l2.2 2.2M5.6 18.4l2.2-2.2M16.2 7.8l2.2-2.2M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  agents: 'M12 3l1.7 4.6L18 9.2l-4.3 1.6L12 15.5l-1.7-4.7L6 9.2l4.3-1.6zM19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8zM5 16l.7 1.8L7.5 19l-1.8.7L5 21.5l-.7-1.8L2.5 19l1.8-.7z',
  documents: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M9 13l2 2 4-4',
  payments: 'M3 7h18v10H3zM3 10h18M7 15h4',
  govsync: 'M17 2v6h-6M7 22v-6h6M17 8a9 9 0 0 1 4 7M7 16a9 9 0 0 1-4-7',
  notifications: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M10.3 21a2 2 0 0 0 3.4 0',
  audit: 'M12 3l7 3v5c0 4.6-3 8.4-7 10-4-1.6-7-5.4-7-10V6zM9 12h6M9 16h4',
  admin: 'M12 4a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM12 16a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM19 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4z',
  integrations: 'M8 8H6a4 4 0 0 0 0 8h2M16 8h2a4 4 0 0 1 0 8h-2M9 12h6',
  cxone: 'M4 6a2 2 0 0 1 2-2h2l2 4-2 1.5a11 11 0 0 0 6.5 6.5L16 14l4 2v2a2 2 0 0 1-2 2A16 16 0 0 1 4 6z',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.3-4.3',
  menu: 'M4 6h16M4 12h16M4 18h16',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.9 2.9l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.2a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.9-2.9l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.2a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.9-2.9l.1.1a1.7 1.7 0 0 0 1.9.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.2a1.7 1.7 0 0 0 1 1.5h.1a1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.9 2.9l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1a1.7 1.7 0 0 0 1.5 1h.2a2 2 0 1 1 0 4h-.2a1.7 1.7 0 0 0-1.5 1z',
  run: 'M6 4.5v15l13-7.5z',
  sparkle: 'M12 3l1.7 4.6L18 9.2l-4.3 1.6L12 15.5l-1.7-4.7L6 9.2l4.3-1.6z',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3.5 2',
  shield: 'M12 3l7 3v5c0 4.6-3 8.4-7 10-4-1.6-7-5.4-7-10V6z',
  chart: 'M4 20V10M10 20V4M16 20v-8M22 20H2',
  pulse: 'M2 12h4l3-8 4 16 3-8h6',
  link: 'M10 14a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7M14 10a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7',
  download: 'M12 3v12M7 10l5 5 5-5M4 21h16',
  filter: 'M4 5h16l-6 7v6l-4 2v-8z',
  plus: 'M12 5v14M5 12h14',
  check: 'm4.5 12.5 5 5 10-11',
  x: 'M6 6l12 12M18 6L6 18',
  'arrow-start': 'M19 12H5M11 18l-6-6 6-6',
  'arrow-end': 'M5 12h14M13 6l6 6-6 6',
  warning: 'M12 3 2.5 20h19zM12 10v4M12 17.5h.01',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 11v5M12 7.5h.01',
  copy: 'M9 9h10v12H9zM5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1',
  print: 'M7 8V3h10v5M7 17H4a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-3M7 14h10v7H7z',
  refresh: 'M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6',
  external: 'M14 4h6v6M20 4 9 15M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6',
  pin: 'M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  star: 'm12 3 2.7 5.6 6 .8-4.4 4.2 1.1 6L12 16.8 6.6 19.6l1.1-6L3.3 9.4l6-.8z',
  user: 'M12 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM5.5 20a6.5 6.5 0 0 1 13 0',
  users: 'M16 19a4 4 0 0 0-8 0M12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19 19a5.5 5.5 0 0 0-3-4.9M5 19a5.5 5.5 0 0 1 3-4.9',
  lock: 'M5 11h14v9H5zM8 11V7.5a4 4 0 0 1 8 0V11M12 15v2',
};

export function Icon({
  name,
  size = 18,
  className,
  style,
}: {
  name: IconName;
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
      style={style}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
